import React from "react";
import {
  ClipboardList,
  ExternalLink,
  FileText,
  FileType,
  MapPin,
} from "lucide-react";
import {
  formatFormDate,
  openFormFile,
  resolveCategoryLabel,
  resolveDestinationCountry,
} from "./companyFormConstants";

const ICON_STROKE = 1.75;

function resolveFileKind(version) {
  const hint = `${version?.file_path || ""} ${version?.file_url || ""}`.toLowerCase();
  if (hint.includes(".pdf")) return "pdf";
  if (hint.includes(".doc") || hint.includes(".docx")) return "word";
  return "file";
}

function MetaLine({ icon: Icon, children }) {
  return (
    <div className="flex min-w-0 items-center gap-2 text-[12px] leading-tight text-[#64748B]">
      <Icon className="shrink-0 text-[#94A3B8]" size={14} strokeWidth={ICON_STROKE} aria-hidden />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

export default function DocumentLibraryCard({ form, categoryLabels }) {
  const version = form.current_version;
  const categoryLabel = resolveCategoryLabel(form.category, categoryLabels);
  const destination = resolveDestinationCountry(form.destination_country);
  const fileKind = resolveFileKind(version);

  const fileTileClass =
    fileKind === "word"
      ? "border-[#BFDBFE] bg-[#EFF6FF] text-[#2563EB]"
      : fileKind === "pdf"
        ? "border-[#FECACA] bg-[#FEF2F2] text-[#DC2626]"
        : "border-[#E2E8F0] bg-[#F8FAFC] text-[#64748B]";

  const FileIcon = fileKind === "word" ? FileType : FileText;

  const btn =
    "inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-lg px-2 text-[11px] font-semibold transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <article className="flex h-full min-w-0 flex-col rounded-xl border border-[#E5ECF5] bg-white p-3.5 shadow-[0_1px_2px_rgba(20,33,61,0.05)]">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${fileTileClass}`}
        >
          <FileIcon size={20} strokeWidth={ICON_STROKE} aria-hidden />
        </div>
        <span className="shrink-0 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[10px] font-semibold text-[#059669]">
          Live
        </span>
      </div>

      <h3 className="mb-2 line-clamp-2 text-[14px] font-semibold leading-snug text-[#14213D]">
        {form.name}
      </h3>

      <div className="space-y-1.5">
        <MetaLine icon={MapPin}>{destination}</MetaLine>
        <MetaLine icon={ClipboardList}>{categoryLabel}</MetaLine>
      </div>

      {version?.approved_at || version?.created_at ? (
        <>
          <div className="my-2.5 border-t border-[#E5ECF5]" />
          <p className="mb-2.5 text-[12px] leading-tight text-[#64748B]">
            Updated {formatFormDate(version.approved_at || version.created_at)}
          </p>
        </>
      ) : null}

      <div className="mt-auto pt-1">
        {version?.file_url ? (
          <button
            type="button"
            onClick={() => openFormFile(version)}
            className={`${btn} border border-[#BFDBFE] bg-[#EFF6FF] text-[#007BFF] hover:bg-[#DBEAFE]/60`}
          >
            <ExternalLink size={12} strokeWidth={ICON_STROKE} aria-hidden />
            Open document
          </button>
        ) : (
          <button
            type="button"
            disabled
            className={`${btn} border border-[#E2E8F0] bg-[#F8FAFC] text-[#94A3B8]`}
          >
            <ExternalLink size={12} strokeWidth={ICON_STROKE} aria-hidden />
            Unavailable
          </button>
        )}
      </div>
    </article>
  );
}
